<template>
  <BaseModal
    card-width="520px"
    no-padding
    no-exit
    @close="$emit('close')"
  >
    <template #toolbar>
      <div class="flex items-start gap-3 w-full px-1 pt-1 pb-1">
        <div class="flex-shrink-0 w-10 h-10 rounded-lg bg-neutral-25 flex items-center justify-center">
          <MonoInstagramIcon
            :width="20"
            :height="20"
            class="text-icon-normal"
          />
        </div>
        <div class="min-w-0 flex-1">
          <BaseText
            type="label"
            size="md"
            class="text-text-loud"
          >
            Save ads from your phone
          </BaseText>
          <BaseText
            type="body"
            size="sm"
            class="text-text-muted mt-1"
          >
            Forward any post to @foreplay_co and it lands in your Foreplay dashboard (Swipe File).
          </BaseText>
          <div
            v-if="isConnected"
            class="ig-connected-pill inline-flex items-center gap-1.5 mt-2.5 pl-1.5 pr-2.5 py-1 rounded-full"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M4.5 10.5L8 14L15.5 6.5"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <BaseText
              type="label"
              size="xs"
            >
              Connected as @{{ getUser.igUsername }}
            </BaseText>
          </div>
        </div>
        <button
          type="button"
          aria-label="Close"
          class="ig-close flex-shrink-0 -mr-1.5 w-7 h-7 rounded-md flex items-center justify-center text-icon-normal hover:bg-neutral-50 hover:text-icon-muted focus:outline-none"
          @click="$emit('close')"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M3.5 3.5L12.5 12.5M12.5 3.5L3.5 12.5"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
            />
          </svg>
        </button>
      </div>
    </template>

    <template #default>
      <div class="border-t border-border-normal">
        <!-- Step 1: connect form -->
        <div
          v-if="step === 1"
          class="px-6 pt-5 pb-2 flex flex-col"
        >
          <BaseInputV2
            v-model="instagramHandle"
            v-validIGHandle
            prefix="@"
            label="Instagram username"
            hint="The account you'll DM from."
            placeholder="foreplay_co"
            :errored="inputError"
            :error-message="'Please add an Instagram username'"
            @input="onHandleInput"
          />

          <div class="mt-5 mb-1.5">
            <BaseText
              type="label"
              size="sm"
              class="text-text-muted"
            >
              Choose a board for your mobile saves.
            </BaseText>
            <BaseText
              type="body"
              size="xs"
              class="text-text-subdued mt-0.5"
            >
              Leave empty to save to general swipe file.
            </BaseText>
          </div>

          <!-- Board picker + one-tap name chips -->
          <div>
            <BaseSingleSelect
              class="mb-1"
              hover-color="transparent"
              no-search
              clear-dropdown
              multiple
              :max-items="4"
              :items="getBoards"
              :selected.sync="selectedBoards"
              item-value="name"
            >
              <template #icon>
                <BoardIcon
                  :width="16"
                  :height="16"
                  class="text-icon-normal -mx-1"
                />
              </template>
              <template #item="{ item }">
                <div
                  class="flex items-center gap-1.5 w-full p-1.5 -my-1.5 rounded-md hover:bg-background-hover cursor-pointer transition-colors duration-70"
                  :class="isBoardSelected(item) ? 'bg-background-normal' : ''"
                >
                  <BoardIcon
                    :width="14"
                    :height="14"
                    class="text-icon-normal flex-shrink-0"
                  />
                  <BaseText
                    type="label"
                    size="xs"
                    class="w-max text-text-muted flex-1 whitespace-nowrap truncate select-none"
                  >
                    {{ item.name }}
                  </BaseText>
                </div>
              </template>
              <template #selection="{ item }">
                <div class="flex items-center border border-border-hover rounded-md px-1.5 py-0.5 bg-background-normal gap-1.5 text-text-muted">
                  <BoardIcon
                    :width="14"
                    :height="14"
                    class="text-icon-normal"
                  />
                  <BaseText
                    type="label"
                    size="xs"
                    class="w-max text-text-muted flex-1 whitespace-nowrap truncate select-none"
                  >
                    {{ item.name }}
                  </BaseText>
                </div>
              </template>
            </BaseSingleSelect>

            <!-- One-tap board names: selects the matching board, or creates it -->
            <div class="flex items-center gap-1.5 flex-wrap mt-2">
              <BaseText
                type="body"
                size="xs"
                class="text-text-subdued mr-0.5"
              >
                One-Click Create Board:
              </BaseText>
              <button
                v-for="suggestion in boardSuggestions"
                :key="`ig-board-suggestion-${suggestion}`"
                type="button"
                class="px-2 py-1 rounded-md border transition-colors duration-100"
                :class="isSuggestionSelected(suggestion)
                  ? 'bg-primary-blue-10 border-primary-blue-25 text-primary-blue-100'
                  : 'bg-white border-border-normal text-text-muted hover:bg-neutral-25'"
                :disabled="!!creatingBoardName"
                @click="applyBoardSuggestion(suggestion)"
              >
                <BaseText
                  type="label"
                  size="xs"
                >
                  {{ creatingBoardName === suggestion ? 'Creating…' : suggestion }}
                </BaseText>
              </button>
            </div>
          </div>
        </div>

        <!-- Step 2: verify. Finish stays disabled until polling sees the DM land. -->
        <div
          v-else
          class="px-6 pt-5 pb-2"
        >
          <!-- Preview first so users see what the DM exchange looks like -->
          <div class="mb-4">
            <BaseText
              type="label"
              size="xs"
              class="uppercase tracking-wide text-text-subdued"
            >
              Preview
            </BaseText>
            <div class="ig-dm-preview mt-2 rounded-xl overflow-hidden border border-border-normal">
              <div class="ig-dm-preview-header flex items-center gap-2 px-3 py-2.5">
                <div class="ig-dm-preview-avatar flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center">
                  <ForeplayIcon
                    :width="14"
                    :height="14"
                  />
                </div>
                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-1">
                    <BaseText
                      type="label"
                      size="xs"
                      class="text-text-loud"
                    >
                      Foreplay
                    </BaseText>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 20 20"
                      fill="none"
                      class="text-primary-blue-100 flex-shrink-0"
                    >
                      <circle
                        cx="10"
                        cy="10"
                        r="9"
                        fill="currentColor"
                      />
                      <path
                        d="M6 10.2L8.6 12.8L14 7.4"
                        stroke="#fff"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                  <BaseText
                    type="body"
                    size="xs"
                    class="text-text-subdued"
                  >
                    @foreplay_co
                  </BaseText>
                </div>
              </div>
              <div class="ig-dm-preview-body px-3 pt-3 pb-3.5 flex flex-col gap-2.5">
                <BaseText
                  type="body"
                  size="xs"
                  class="text-center text-text-subdued"
                >
                  Today
                </BaseText>
                <div class="flex justify-end">
                  <div class="ig-dm-bubble-sent px-3.5 py-2 rounded-2xl">
                    <BaseText
                      type="label"
                      size="sm"
                      class="text-white tracking-widest font-mono tabular-nums"
                    >
                      {{ verificationCode || '------' }}
                    </BaseText>
                  </div>
                </div>
                <div class="flex items-end gap-1.5">
                  <div class="ig-dm-preview-avatar flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center">
                    <ForeplayIcon
                      :width="10"
                      :height="10"
                    />
                  </div>
                  <div class="ig-dm-bubble-received px-3 py-2 rounded-2xl min-w-0">
                    <BaseText
                      type="label"
                      size="xs"
                      class="text-text-loud whitespace-pre-line"
                    >
                      🎉 Foreplay account linked successfully 🎉
                    </BaseText>
                    <BaseText
                      type="body"
                      size="xs"
                      class="text-text-muted mt-1"
                    >
                      You can now save ads to Foreplay directly from the Instagram mobile app by simply DMing the ad/post you want to save to foreplay_co.
                    </BaseText>
                  </div>
                </div>
                <BaseText
                  type="body"
                  size="xs"
                  class="text-text-subdued pl-6"
                >
                  Seen just now
                </BaseText>
              </div>
            </div>
          </div>

          <!-- 1/2/3 treatment matching the walkthrough intro card -->
          <div
            class="ig-verify-steps relative rounded-lg bg-neutral-10 border border-border-normal px-4 py-3.5"
            style="--ig-step-accent: #1F69FF"
          >
            <div
              class="ig-steps-line"
              aria-hidden="true"
            >
              <span class="ig-steps-line-flow" />
            </div>
            <ol class="flex flex-col gap-3.5">
              <li class="flex items-start gap-3 ml-0">
                <div
                  class="relative z-10 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center"
                  :class="stepBadgeClass(1)"
                >
                  <BaseText
                    type="label"
                    size="xs"
                    :class="stepNumberClass(1)"
                  >
                    1
                  </BaseText>
                </div>
                <div class="min-w-0 flex-1">
                  <BaseText
                    type="label"
                    size="sm"
                    :class="stepTitleClass(1)"
                  >
                    Copy your one-time code
                  </BaseText>
                  <div class="mt-2 flex items-center gap-2 pl-3 pr-1.5 py-1.5 rounded-lg border border-border-normal bg-white">
                    <BaseText
                      type="heading"
                      size="md"
                      class="text-text-loud tracking-widest font-mono flex-grow tabular-nums"
                    >
                      {{ verificationCode }}
                    </BaseText>
                    <BaseButton
                      primary
                      class="w-28 flex-shrink-0"
                      @click="copyCode"
                    >
                      {{ codeCopied ? 'Copied' : 'Copy code' }}
                    </BaseButton>
                  </div>
                </div>
              </li>
              <li class="flex items-start gap-3 ml-0">
                <div
                  class="relative z-10 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center"
                  :class="stepBadgeClass(2)"
                >
                  <BaseText
                    type="label"
                    size="xs"
                    :class="stepNumberClass(2)"
                  >
                    2
                  </BaseText>
                </div>
                <div class="min-w-0 flex-1">
                  <button
                    type="button"
                    class="ig-step-open inline-flex items-center gap-1 max-w-full text-left focus:outline-none"
                    @click="openInstagram"
                  >
                    <BaseText
                      type="label"
                      size="sm"
                      :class="stepTitleClass(2)"
                    >
                      DM it to @foreplay_co
                    </BaseText>
                    <OpenExternalIcon
                      :width="12"
                      :height="12"
                      class="flex-shrink-0 text-icon-normal"
                    />
                  </button>
                  <BaseText
                    type="body"
                    size="sm"
                    class="text-text-muted mt-0.5"
                  >
                    Paste the code into a new Instagram DM. We'll link the account the moment it arrives.
                  </BaseText>
                </div>
              </li>
              <li class="flex items-start gap-3 ml-0">
                <div
                  class="relative z-10 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center"
                  :class="stepBadgeClass(3)"
                >
                  <BaseText
                    type="label"
                    size="xs"
                    :class="stepNumberClass(3)"
                  >
                    3
                  </BaseText>
                </div>
                <div class="min-w-0 flex-1">
                  <BaseText
                    type="label"
                    size="sm"
                    :class="stepTitleClass(3)"
                  >
                    Forward any ad from your feed
                  </BaseText>
                  <BaseText
                    type="body"
                    size="sm"
                    class="text-text-muted mt-0.5"
                  >
                    From then on, share a reel or post to @foreplay_co and it shows up here with the video, copy and transcript.
                  </BaseText>
                </div>
              </li>
            </ol>
          </div>

          <!-- Status: waiting strip until the DM arrives, then the Connected pill -->
          <div
            class="mt-4 flex items-center"
            style="min-height: 52px"
          >
            <div
              v-if="verificationConfirmed"
              class="ig-connected-pill inline-flex items-center gap-1.5 pl-1.5 pr-2.5 py-1 rounded-full"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M4.5 10.5L8 14L15.5 6.5"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <BaseText
                type="label"
                size="xs"
              >
                {{ connectedLabel }}
              </BaseText>
            </div>
            <div
              v-else
              class="flex items-center gap-2.5 w-full px-3 py-2.5 rounded-lg border border-border-normal bg-neutral-10"
            >
              <BaseLoadingSpinnerCircle
                :width="16"
                :height="16"
                class="flex-shrink-0 text-icon-normal"
              />
              <div class="min-w-0">
                <BaseText
                  type="label"
                  size="sm"
                  class="text-text-muted"
                >
                  Waiting for your DM…
                </BaseText>
                <BaseText
                  type="body"
                  size="xs"
                  class="text-text-subdued"
                >
                  We'll connect the moment the code arrives.
                </BaseText>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="pt-4 pb-5 px-6 border-t border-border-normal mt-4 flex items-center justify-end gap-2">
          <template v-if="step === 1">
            <BaseButton
              primary
              class="w-44"
              :destroy="getUser.igUsername?.length && !instagramHandle"
              :disabled="isButtonDisabled"
              :loading="loading"
              @click="connectInstagram"
            >
              {{ getButtonText }}
            </BaseButton>
          </template>
          <!-- Only the confirmed DM closes the modal: opening Instagram is an
          action, not a way past this step, so Finish stays disabled until the
          poll sees the DM land. -->
          <template v-else>
            <BaseButton
              v-if="!verificationConfirmed"
              outlined
              class="ig-open-btn"
              @click="openInstagram"
            >
              <span class="inline-flex items-center gap-1.5">
                Open Instagram
                <OpenExternalIcon
                  :width="14"
                  :height="14"
                  class="flex-shrink-0"
                />
              </span>
            </BaseButton>
            <BaseButton
              primary
              class="w-32"
              :disabled="!verificationConfirmed"
              @click="finishVerification"
            >
              Finish
            </BaseButton>
          </template>
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import FirebaseAPI from '@/api/firebase'
import firebase from '../../../api/config/FirebaseConfig'
import { IG_BOARD_SUGGESTIONS } from '@/utils/setup/setupContent'
import MonoInstagramIcon from '../../globals/Icons/socials/mono/MonoInstagramIcon.vue'
import ForeplayIcon from '../../globals/Icons/ForeplayIcon.vue'
import BoardIcon from '../../globals/Icons/BoardIcon.vue'
import OpenExternalIcon from '../../globals/Icons/OpenExternalIcon.vue'

const POLL_INTERVAL_MS = 3000

// Board names are stored as '#snake_case' — compare on the readable form so a
// suggestion matches a board the user already has
const boardNameKey = (name) => String(name || '')
  .replace(/^#/, '')
  .replace(/[\s_]+/g, ' ')
  .trim()
  .toLowerCase()

const titleCase = (raw) => String(raw || '')
  .trim()
  .replace(/\S+/g, (word) => word[0].toUpperCase() + word.slice(1).toLowerCase())

const sanitizeHandle = (raw) => {
  if (!raw) return ''
  let value = String(raw).trim()
  value = value.replace(/^@+/, '')
  value = value.replace(/^https?:\/\/(www\.)?instagram\.com\//i, '')
  value = value.replace(/\/.*$/, '')
  value = value.replace(/\s+/g, '')
  return value
}

export default {
  name: 'InstagramModal',
  components: {
    MonoInstagramIcon,
    ForeplayIcon,
    BoardIcon,
    OpenExternalIcon
  },
  directives: {
    validIGHandle: {
      inserted: function (el, binding, vnode) {
        el.addEventListener('keypress', function (e) {
          const key = String.fromCharCode(e.keyCode)
          if (!/[a-zA-Z0-9_.]/.test(key)) {
            e.preventDefault()
            vnode.context.setDisallowedKeyFlag(true)
          }
        })
      }
    }
  },
  props: {
    // Guided setup walkthrough: always open on step 1 (handle + boards),
    // even when the user is already waiting on verification.
    forceConnectStep: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      instagramHandle: '',
      selectedBoards: [],
      creatingBoardName: null,
      inputError: false,
      loading: false,
      verificationCode: null,
      step: 1,
      disallowedKeyUsed: false,
      codeCopied: false,
      codeShared: false,
      verificationConfirmed: false,
      pollTimer: null
    }
  },
  computed: {
    ...mapGetters('BoardsModule', ['getBoards']),
    ...mapGetters('AuthModule', ['getUser', 'getTeam', 'getUserFirstName']),
    // Personal chip first when we know the user's name, then the shared list
    boardSuggestions () {
      const firstName = titleCase(this.getUserFirstName)
      const names = firstName
        ? [`${firstName}'s Saves`, ...IG_BOARD_SUGGESTIONS]
        : [...IG_BOARD_SUGGESTIONS]
      const seen = new Set()
      return names.filter((name) => {
        const key = boardNameKey(name)
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
    },
    // Which of the verify steps is highlighted: copying the code advances
    // to the DM, and the confirmed DM advances to forwarding ads
    activeVerifyStep () {
      if (this.verificationConfirmed) return 3
      return this.codeShared ? 2 : 1
    },
    isConnected () {
      return !!(this.getUser.igUsername && !this.getUser.igWaitingVerification)
    },
    connectedLabel () {
      const handle = this.getUser.igUsername || this.instagramHandle
      return handle ? `Connected as @${handle}` : 'Connected'
    },
    boardsUnchanged () {
      const selectedBoardIds = this.selectedBoards.map((board) => board.id)
      return !!(this.getUser.igBoardIds &&
        selectedBoardIds.every((id) => this.getUser.igBoardIds.includes(id)) &&
        this.getUser.igBoardIds.every((id) => selectedBoardIds.includes(id)))
    },
    isButtonDisabled () {
      // Guided connect always needs a handle so Connect / Continue can
      // move to the verify step.
      if (this.forceConnectStep) {
        return !this.instagramHandle
      }
      return (!this.getUser.igUsername?.length && !this.instagramHandle) ||
        (this.instagramHandle === this.getUser.igUsername && this.boardsUnchanged)
    },
    getButtonText () {
      if (this.forceConnectStep) {
        if (this.getUser.igUsername && !this.instagramHandle) return 'Remove IG'
        if (
          this.getUser.igWaitingVerification &&
          this.instagramHandle === this.getUser.igUsername &&
          this.boardsUnchanged
        ) {
          return 'Continue'
        }
        return 'Connect IG'
      }
      let buttonText = 'Requires Username'
      if (this.getUser.igUsername) {
        buttonText = 'Change IG'
      } else {
        buttonText = 'Connect IG'
      }
      if (!this.boardsUnchanged && this.instagramHandle === this.getUser.igUsername) {
        buttonText = 'Change Boards'
      }
      if (this.getUser.igUsername && !this.instagramHandle) {
        buttonText = 'Remove IG'
      }
      return buttonText
    }
  },
  async mounted () {
    const db = firebase.firestore()
    const user = firebase.auth().currentUser
    const userData = await db.collection('auth-users').doc(user.uid).get()
    this.SET_USER({ ...this.getUser, ...userData.data() })

    this.verificationCode = this.getUser.igVerificationCode ||
      (Math.floor(100000 + Math.random() * 900000)).toString()
    if (this.getUser.igUsername) {
      this.instagramHandle = this.getUser.igUsername
    }
    if (this.getUser.igBoardIds && this.getUser.igBoardIds.length) {
      this.selectedBoards = this.getBoards.filter((board) => {
        return this.getUser.igBoardIds.includes(board.id)
      })
    }
    // Default (settings / sidebar): jump to verify if already waiting.
    // Guided setup: stay on step 1 (handle + boards) even when already waiting.
    if (
      !this.forceConnectStep &&
      this.getUser.igWaitingVerification &&
      this.getUser.igUsername
    ) {
      this.step = 2
      this.startPolling()
    }
  },
  beforeDestroy () {
    this.stopPolling()
    clearTimeout(this.copyTimer)
  },
  methods: {
    ...mapMutations('AuthModule', ['SET_USER_IG_BOARD_IDS', 'SET_USER_IG_USERNAME', 'SET_USER']),
    ...mapActions('BoardsModule', ['fetchBoards']),
    setDisallowedKeyFlag (value) {
      this.disallowedKeyUsed = value
    },
    isBoardSelected (item) {
      return this.selectedBoards.some((board) => board.id === item.id)
    },
    stepBadgeClass (number) {
      return number === this.activeVerifyStep
        ? 'bg-primary-blue-100'
        : 'bg-white border border-border-normal'
    },
    stepNumberClass (number) {
      return number === this.activeVerifyStep ? 'text-white' : 'text-text-subdued'
    },
    stepTitleClass (number) {
      return number === this.activeVerifyStep ? 'text-text-loud' : 'text-text-muted'
    },
    isSuggestionSelected (suggestion) {
      const key = boardNameKey(suggestion)
      return this.selectedBoards.some((board) => boardNameKey(board.name) === key)
    },
    // Chip click: select the board if it exists, otherwise create it and select
    // the new one (same payload shape as ManageBoardModal)
    async applyBoardSuggestion (suggestion) {
      if (this.creatingBoardName) return
      const key = boardNameKey(suggestion)
      const existing = this.getBoards.find((board) => boardNameKey(board.name) === key)
      if (existing) {
        if (!this.isBoardSelected(existing)) {
          this.selectedBoards = [...this.selectedBoards, existing]
        }
        return
      }

      this.creatingBoardName = suggestion
      try {
        const payload = {
          ads: [],
          description: '',
          name: `#${suggestion.replace(/ /g, '_').toLowerCase().trim()}`
        }
        if (this.getTeam) payload.teamId = this.getTeam.id

        const newBoardId = await FirebaseAPI.Boards.create(payload)
        await this.fetchBoards()
        const created = this.getBoards.find((board) => board.id === newBoardId)
        if (created) this.selectedBoards = [...this.selectedBoards, created]
        this.$showAlert({
          message: 'Board created successfully',
          type: 'success'
        })
      } catch (error) {
        console.error('Error creating Instagram board', error)
        this.$showAlert({
          message: 'Error creating board',
          type: 'error'
        })
      } finally {
        this.creatingBoardName = null
      }
    },
    onHandleInput (value) {
      this.inputError = false
      const cleaned = sanitizeHandle(value)
      if (cleaned !== value) this.instagramHandle = cleaned
    },
    async copyCode () {
      await this.$copyText(this.verificationCode)
      this.codeCopied = true
      this.codeShared = true
      clearTimeout(this.copyTimer)
      this.copyTimer = setTimeout(() => { this.codeCopied = false }, 1800)
    },
    openInstagram () {
      window.open('https://www.instagram.com/foreplay_co/', '_blank')
    },
    finishVerification () {
      // Only reachable once polling has seen the DM land, so the modal never
      // closes on an unverified account.
      this.$emit('connected')
      this.$emit('refresh')
      this.$emit('close')
    },
    startPolling () {
      this.stopPolling()
      this.pollTimer = setInterval(this.pollVerification, POLL_INTERVAL_MS)
    },
    stopPolling () {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    async pollVerification () {
      try {
        const user = firebase.auth().currentUser
        if (!user) return
        const snap = await firebase.firestore().collection('auth-users').doc(user.uid).get()
        const data = snap.data() || {}
        this.SET_USER({ ...this.getUser, ...data })
        if (data.igUsername && !data.igWaitingVerification) {
          this.verificationConfirmed = true
          this.stopPolling()
        }
      } catch (error) {
        console.error('Error polling Instagram verification', error)
      }
    },
    async connectInstagram () {
      this.loading = true

      if (this.getUser.igUsername && !this.instagramHandle) {
        await this.disconnectInstagram()
        this.loading = false
        this.$emit('close')
        return
      }

      const user = firebase.auth().currentUser
      const boardIds = this.selectedBoards.map((board) => board.id)
      await FirebaseAPI.Users.update(user.uid, { igBoardIds: boardIds })
      this.SET_USER_IG_BOARD_IDS(boardIds)

      // Guided: Continue with unchanged handle/boards still advances to verify
      if (
        this.forceConnectStep &&
        this.boardsUnchanged &&
        this.instagramHandle === this.getUser.igUsername &&
        this.getUser.igWaitingVerification
      ) {
        this.loading = false
        this.step = 2
        this.startPolling()
        return
      }

      this.$emit('refresh')
      if (this.boardsUnchanged && this.instagramHandle === this.getUser.igUsername) {
        this.loading = false
        this.$showAlert({
          message: 'Instagram boards updated',
          type: 'success'
        })
        this.$emit('close')
        return
      }
      if (this.instagramHandle) {
        const username = sanitizeHandle(this.instagramHandle)
        await FirebaseAPI.Users.update(user.uid, {
          igUsername: username,
          igVerificationCode: `${this.verificationCode}`,
          igWaitingVerification: true
        })
        this.SET_USER_IG_USERNAME(username)
        this.loading = false
        this.step = 2
        this.startPolling()
        this.$emit('refresh')
      } else {
        this.$emit('close')
        this.$emit('refresh')
      }
    },
    async disconnectInstagram () {
      const user = firebase.auth().currentUser
      await FirebaseAPI.Users.update(user.uid, {
        igUsername: firebase.firestore.FieldValue.delete(),
        igVerificationCode: firebase.firestore.FieldValue.delete(),
        igWaitingVerification: firebase.firestore.FieldValue.delete(),
        igBoardIds: firebase.firestore.FieldValue.delete(),
        igConversationId: firebase.firestore.FieldValue.delete()
      })
      this.SET_USER_IG_BOARD_IDS([])
      this.SET_USER_IG_USERNAME(null)
      this.$emit('refresh')
    }
  }
}
</script>

<style scoped>
.ig-close {
  transition: background-color 150ms ease, color 150ms ease;
}
.ig-connected-pill {
  color: #00A878; /* primary-green-100 */
  background-color: rgba(0, 168, 120, 0.10);
}

/* Vertical connector: badge centres, matching SetupTourIntroModal */
.ig-steps-line {
  position: absolute;
  left: 25px;
  top: 24px;
  bottom: 24px;
  width: 2px;
  border-radius: 9999px;
  background-color: #DFE1E6; /* neutral-100 */
  overflow: hidden;
}
.ig-steps-line-flow {
  position: absolute;
  left: 0;
  right: 0;
  height: 45%;
  border-radius: 9999px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0) 0%,
    var(--ig-step-accent, #1F69FF) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  animation: ig-line-flow 2.4s cubic-bezier(0.45, 0, 0.55, 1) infinite;
}
@keyframes ig-line-flow {
  0% {
    transform: translateY(-110%);
    opacity: 0;
  }
  15% {
    opacity: 1;
  }
  85% {
    opacity: 1;
  }
  100% {
    transform: translateY(260%);
    opacity: 0;
  }
}

.ig-dm-preview {
  background-color: #FFFFFF;
}
.ig-dm-preview-header {
  background-color: #FAFAFA;
  border-bottom: 1px solid #DFE1E6; /* border-normal */
}
.ig-dm-preview-avatar {
  background-color: #06070F; /* neutral-900 */
}
.ig-dm-bubble-sent {
  /* Native Instagram DM purple */
  background-color: #5851DB;
  max-width: 85%;
  animation: ig-dm-in 360ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
}
.ig-dm-bubble-received {
  background-color: #EFEFEF;
  max-width: 88%;
  animation: ig-dm-in 360ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
  animation-delay: 420ms;
}
.ig-step-open:hover :deep(.text-text-loud),
.ig-step-open:hover :deep(.text-text-muted) {
  color: #1F69FF; /* primary-blue-100 */
}
.ig-step-open:hover .text-icon-normal {
  color: #1F69FF;
}
.ig-open-btn {
  min-width: 10.5rem;
}
@keyframes ig-dm-in {
  from {
    opacity: 0;
    transform: translateY(6px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
@media (prefers-reduced-motion: reduce) {
  .ig-steps-line-flow,
  .ig-dm-bubble-sent,
  .ig-dm-bubble-received {
    animation: none;
  }
}
</style>
